import type { TemplateDefinition } from '@/api/template-api';
import { baselineFromDemandScaling } from '@/domain/templates/demand-scaling';
import { withScalingMetric } from '@/domain/templates/staffing';
import type { WizardState } from '@/types/template';

/**
 * Maps the UI `WizardState` to the persisted `TemplateDefinition`. The wizard
 * edits contract shapes directly (`ShiftSet` = `ShiftPattern` + UI extras,
 * `RotaTemplateScope`, `SchedulingRuleset`), so this is a near-identity assembly.
 *
 * Demand scaling is the one transform: the UI `demandScaling` settings are
 * persisted as-is (for reload) and the contract `baseline` is **derived** from
 * them for rotagen, while every scaling shift's metric is forced to the template
 * metric (the UI has no per-shift metric).
 *
 * Lifecycle (draft/published) is server-owned and never written into the
 * definition.
 *
 * @param state - The current wizard state from the UI
 */
export function mapWizardStateToContract(state: WizardState): TemplateDefinition {
  const demandScaling = state.templateBasics.demandScaling;
  const metric = demandScaling?.metric;
  return {
    name: state.templateBasics.name,
    type: state.templateBasics.type,
    description: state.templateBasics.description,
    scope: state.templateBasics.scope,
    shifts: metric ? state.shifts.map((shift) => withScalingMetric(shift, metric)) : state.shifts,
    schedulingRules: state.schedulingRules,
    demandScaling,
    baseline: baselineFromDemandScaling(demandScaling),
    metadata: {
      totalShifts: state.shifts.length,
    },
  };
}
